import React, { useEffect, useState } from "react";
import { Link, Redirect, useParams } from "react-router-dom";
import Header from "@/component/header";
import { videoRepo } from "@/repo/video-repo";
import { ASSETS_BASE_URL } from "@/utils/constants";
import loadingGif from "@public/img/loading.gif";
import microphoneIcon from "@public/img/microphone.svg";
import subtitleIcon from "@public/img/subtitle.svg";
import imdbIcon from "@public/img/imdb.svg";
import metacriticIcon from "@public/img/metacritic.svg";
import rottenTomatoesIcon from "@public/img/tomatometer.svg";
import mdlIcon from "@public/img/mdl.webp";
import malIcon from "@public/img/mal.webp";
import timeIcon from "@public/img/clock-four.svg";
import calendarIcon from "@public/img/calendar.svg";
import flagIcon from "@public/img/flag.svg";
import globeIcon from "@public/img/globe.svg";
import MovieAgents from "@/component/parts/movie-details/movie-agents";
import MovieLikes from "@/component/parts/movie-details/movie-likes";
import { MovieCommentData, MovieDetails } from "@/entry/server/movie";
import SeriesDlBox from "@/component/parts/movie-details/series-dlbox";
import MovieDlBox from "@/component/parts/movie-details/movie-dlbox";
import Footer from "@/component/footer";
import RelatedMovies from "@/component/parts/movie-details/related-movies";
import Comments from "@/component/parts/movie-details/comments";
import { Pagination } from "@/lib/api";
import BookmarkButton from "./_components/bookmark";
import ReportsButton from "./_components/reports";
import MediaActionBar from "./_components/media-action-bar";
import "@/styles/movie-detail.css";

const VALID_TYPES = ["movie", "series", "animation", "anime"];

const EMPTY_PAGINATION: Pagination = {
    has_more: false,
    next_page: 1,
    previous_page: 1,
    current_page: 1,
    per_page: 10,
    total_items: 0,
    total_pages: 0,
    limit: 10,
};

export default function Page() {
    const params = useParams<{ type: string; id: string; slug: string }>();
    const type = params.type ? params.type : "";
    const id = params.id ? params.id : "";
    const slug = params.slug ? params.slug : "";

    const [data, setData] = useState<MovieDetails | null>(null);
    const [defaultComments, setDefaultComments] = useState<MovieCommentData[]>([]);
    const [commentsPagination, setCommentsPagination] = useState<Pagination>(EMPTY_PAGINATION);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    const isValidType = VALID_TYPES.indexOf(type) !== -1;
    const numericId = Number(id);
    const isValidId = !isNaN(numericId);

    useEffect(function () {
        if (!isValidType || !isValidId) {
            setLoading(false);
            setNotFound(true);
            return;
        }

        var alive = true;
        setLoading(true);
        setNotFound(false);
        setData(null);

        videoRepo.getDetails(numericId).then(function (videoDetails) {
            if (!alive) {
                return;
            }
            if (!videoDetails.status || !videoDetails.data) {
                setNotFound(true);
                setLoading(false);
                return;
            }
            setData(videoDetails.data);
            return videoRepo.getComments(videoDetails.data.id, "false", 1);
        }).then(function (commentsRes) {
            if (!alive) {
                return;
            }
            if (commentsRes && commentsRes.status) {
                setDefaultComments(commentsRes.data ? commentsRes.data : []);
                setCommentsPagination(
                    commentsRes.pagination ? commentsRes.pagination : EMPTY_PAGINATION
                );
            }
            setLoading(false);
        }).catch(function () {
            if (!alive) {
                return;
            }
            setNotFound(true);
            setLoading(false);
        });

        return function () {
            alive = false;
        };
    }, [type, id, slug, isValidType, isValidId, numericId]);

    if (!isValidType || !isValidId) {
        return <Redirect to="/404" />;
    }

    if (loading) {
        return (
            <div className="movie-detail-page__loading">
                <img src={loadingGif} alt="loading" />
            </div>
        );
    }

    if (notFound || !data) {
        return <Redirect to="/404" />;
    }

    return (
        <div className="movie-detail-page">
            <TopSection data={data} />
            <div role="main" className="movie-detail-page__main container">
                {data.is_series && <SeriesDlBox id={data.id} />}
                {!data.is_series && <MovieDlBox id={data.id} />}
                <MoreInfo data={data} />
                <RelatedMovies id={data.id} />
                <Comments
                    movieId={data.id}
                    defaultComments={defaultComments}
                    commentsPagination={commentsPagination}
                />
            </div>
            <Footer />
        </div>
    );
}

function TopSection({ data }: { data: MovieDetails }) {
    var bannerSrc = data.banner.indexOf("http") === 0
        ? data.banner
        : ASSETS_BASE_URL + data.banner;
    var thumbnailSrc = data.thumbnail.indexOf("http") === 0
        ? data.thumbnail
        : ASSETS_BASE_URL + data.thumbnail;
    var titleAlt = data.title ? data.title : "";
    var ageLabel = data.age_rating === "not rated" ? "نامشخص" : data.age_rating;

    return (
        <>
            <section className="movie-detail-hero">
                <div className="movie-detail-hero__banner">
                    <div className="movie-detail-hero__banner-img">
                        <img src={bannerSrc} alt={titleAlt} />
                    </div>
                    <div className="movie-detail-hero__overlay" />
                    <Header />

                    <div className="movie-detail-hero__body">
                        <div className="movie-detail-hero__inner">
                            <div className="movie-detail-hero__poster-col">
                                <div className="movie-detail-hero__poster">
                                    <img src={thumbnailSrc} alt={titleAlt} />
                                </div>
                            </div>

                            <div className="movie-detail-hero__content-col">
                                <div className="movie-detail-hero__title-row">
                                    {(data.is_dubbed || data.is_subbed) && (
                                        <div className="movie-detail-hero__media-badges">
                                            {data.is_dubbed && (
                                                <img
                                                    src={microphoneIcon}
                                                    alt={"دانلود " + data.title + " با دوبله فارسی"}
                                                    className="movie-detail-hero__media-badge movie-detail-hero__media-badge--dub"
                                                />
                                            )}
                                            {data.is_subbed && (
                                                <img
                                                    src={subtitleIcon}
                                                    alt={"دانلود " + data.title + " با زیرنویس فارسی"}
                                                    className="movie-detail-hero__media-badge movie-detail-hero__media-badge--sub"
                                                />
                                            )}
                                        </div>
                                    )}
                                    <h1 className="movie-detail-hero__title">{data.title}</h1>
                                </div>

                                {data.fa_title && data.fa_title !== "" && (
                                    <p className="movie-detail-hero__subtitle">{data.fa_title}</p>
                                )}

                                <div className="movie-detail-hero__ratings">
                                    <div className="movie-detail-hero__rating-item movie-detail-hero__rating-item--imdb">
                                        <a
                                            className="movie-detail-hero__rating-link"
                                            href={"https://www.imdb.com/title/" + data.imdb_id}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <img src={imdbIcon} alt="IMDB" />
                                            <span className="movie-detail-hero__rating-score" dir="ltr">
                                                {data.imdb_rate > 0 ? data.imdb_rate + " / 10" : "N/A"}
                                            </span>
                                        </a>
                                        {data.imdb_votes > 0 && (
                                            <span className="movie-detail-hero__rating-votes">
                                                {data.imdb_votes + " رای"}
                                            </span>
                                        )}
                                    </div>

                                    {data.metacritic > 0 && (
                                        <div className="movie-detail-hero__rating-item movie-detail-hero__rating-item--score">
                                            <img src={metacriticIcon} alt="Metacritic" />
                                            <span className="movie-detail-hero__rating-score" dir="ltr">
                                                {data.metacritic}
                                            </span>
                                        </div>
                                    )}

                                    {data.rotten_tomatoes > 0 && (
                                        <div className="movie-detail-hero__rating-item movie-detail-hero__rating-item--score">
                                            <img src={rottenTomatoesIcon} alt="Rotten Tomatoes" />
                                            <span className="movie-detail-hero__rating-score" dir="ltr">
                                                {data.rotten_tomatoes}
                                            </span>
                                        </div>
                                    )}

                                    {data.mdl > 0 && (
                                        <div className="movie-detail-hero__rating-item movie-detail-hero__rating-item--score">
                                            <img src={mdlIcon} alt="MDL" />
                                            <span className="movie-detail-hero__rating-score" dir="ltr">
                                                {data.mdl}
                                            </span>
                                        </div>
                                    )}

                                    {data.mal > 0 && (
                                        <div className="movie-detail-hero__rating-item movie-detail-hero__rating-item--score">
                                            <img src={malIcon} alt="MAL" />
                                            <span className="movie-detail-hero__rating-score" dir="ltr">
                                                {data.mal}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {data.description && (
                                    <p className="movie-detail-hero__description">{data.description}</p>
                                )}

                                <div className="movie-detail-hero__tags">
                                    <span className="movie-detail-hero__tag movie-detail-hero__tag--age">
                                        {ageLabel}
                                    </span>

                                    <span className="movie-detail-hero__tag-dot" />

                                    {data.genres.map(function (genre, index) {
                                        return (
                                            <Link
                                                key={index + "-genre"}
                                                to={"/genre/" + genre.slug}
                                                className="movie-detail-hero__tag movie-detail-hero__tag--genre"
                                            >
                                                {genre.name}
                                            </Link>
                                        );
                                    })}

                                    {data.update_text !== "" && (
                                        <span className="movie-detail-hero__tag movie-detail-hero__tag--update">
                                            {data.update_text}
                                        </span>
                                    )}
                                </div>

                                <div className="movie-detail-hero__meta">
                                    {data.duration > 0 && (
                                        <span className="movie-detail-hero__meta-item">
                                            <img src={timeIcon} alt="" />
                                            {data.duration + " دقیقه"}
                                        </span>
                                    )}
                                    {data.release_date && (
                                        <span className="movie-detail-hero__meta-item">
                                            <img src={calendarIcon} alt="" />
                                            {data.release_date}
                                        </span>
                                    )}
                                    {data.languages.length > 0 && (
                                        <span className="movie-detail-hero__meta-item">
                                            <img src={flagIcon} alt="" />
                                            {"زبان "}
                                            {data.languages.map(function (language, index) {
                                                return (
                                                    <Link
                                                        key={index + "-language"}
                                                        to={"/language/" + language.slug}
                                                    >
                                                        {language.name}
                                                        {index < data.languages.length - 1 ? "، " : ""}
                                                    </Link>
                                                );
                                            })}
                                        </span>
                                    )}
                                    {data.countries.length > 0 && (
                                        <span className="movie-detail-hero__meta-item">
                                            <img src={globeIcon} alt="" />
                                            {data.countries.map(function (country, index) {
                                                return (
                                                    <Link
                                                        key={index + "-country"}
                                                        to={"/country/" + country.slug}
                                                    >
                                                        {country.name}
                                                        {index < data.countries.length - 1 ? "، " : ""}
                                                    </Link>
                                                );
                                            })}
                                        </span>
                                    )}
                                    {data.networks.length > 0 && (
                                        <span className="movie-detail-hero__meta-item">
                                            <img src={flagIcon} alt="" />
                                            {"شبکه: "}
                                            {data.networks.map(function (network, index) {
                                                return (
                                                    <Link
                                                        key={index + "-network"}
                                                        to={"/network/" + network.slug}
                                                    >
                                                        {network.name}
                                                        {index < data.networks.length - 1 ? "، " : ""}
                                                    </Link>
                                                );
                                            })}
                                        </span>
                                    )}
                                </div>
                                
                                <MediaActionBar
                                    movieId={data.id}
                                    movieTitle={data.title}
                                    isSeries={data.is_series}
                                    isBookmarkedDefault={data.is_in_favorites}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <MovieLikes
                    stats={data.stats}
                    is_liked={data.is_liked}
                    is_disliked={data.is_disliked}
                    id={data.id}
                    title={data.title}
                    trailerUrl={data.trailer}
                    hasTrailer={Boolean(data.trailer)}
                />
            </section>
        </>
    );
}

function MoreInfo({ data }: { data: MovieDetails }) {
    return (
        <>
            <h2 className="movie-detail-section-title">اطلاعات بیشتر</h2>
            <section className="movie-detail-more">
                <MoreInfoRow label="کشور:">
                    {data.countries.length > 0 ? (
                        data.countries.map(function (country, index) {
                            return (
                                <Link
                                    key={index + "-country"}
                                    to={"/country/" + country.slug}
                                    className="movie-detail-more__pill"
                                >
                                    {country.name}
                                </Link>
                            );
                        })
                    ) : (
                        <span className="movie-detail-more__muted">نامشخص</span>
                    )}
                </MoreInfoRow>

                <MoreInfoRow label="زبان:">
                    {data.languages.length > 0 ? (
                        data.languages.map(function (language, index) {
                            return (
                                <Link
                                    key={index + "-language"}
                                    to={"/language/" + language.slug}
                                    className="movie-detail-more__pill"
                                >
                                    {language.name}
                                </Link>
                            );
                        })
                    ) : (
                        <span className="movie-detail-more__muted">نامشخص</span>
                    )}
                </MoreInfoRow>

                <MoreInfoRow label="ژانر:">
                    {data.genres.length > 0 ? (
                        data.genres.map(function (genre, index) {
                            return (
                                <Link
                                    key={index + "-genre"}
                                    to={"/genre/" + genre.slug}
                                    className="movie-detail-more__pill"
                                >
                                    {genre.name}
                                </Link>
                            );
                        })
                    ) : (
                        <span className="movie-detail-more__muted">نامشخص</span>
                    )}
                </MoreInfoRow>

                <MoreInfoRow label="سال انتشار:">
                    {data.year > 0 ? (
                        <Link to={"/year/" + data.year} className="movie-detail-more__pill">
                            {data.year}
                        </Link>
                    ) : (
                        <span className="movie-detail-more__muted">نامشخص</span>
                    )}
                </MoreInfoRow>

                <MoreInfoRow label="امتیاز IMDB:">
                    <span className={"movie-detail-more__text ltr" + (data.imdb_rate <= 0 ? " movie-detail-more__muted" : "")}>
                        {data.imdb_rate > 0 ? data.imdb_rate + " / 10" : "نامشخص"}
                    </span>
                </MoreInfoRow>

                <MoreInfoRow label="تعداد رای:">
                    <span className={"movie-detail-more__text ltr" + (data.imdb_votes <= 0 ? " movie-detail-more__muted" : "")}>
                        {data.imdb_votes > 0 ? data.imdb_votes + " رای" : "نامشخص"}
                    </span>
                </MoreInfoRow>

                <MoreInfoRow label="زمان:">
                    <span className={"movie-detail-more__text ltr" + (data.duration <= 0 ? " movie-detail-more__muted" : "")}>
                        {data.duration > 0 ? data.duration + " دقیقه" : "نامشخص"}
                    </span>
                </MoreInfoRow>

                {data.is_series && (
                    <MoreInfoRow label="شبکه پخش:">
                        {data.networks.length > 0 ? (
                            data.networks.map(function (network, index) {
                                return (
                                    <Link
                                        key={index + "-network"}
                                        to={"/network/" + network.slug}
                                        className="movie-detail-more__pill"
                                    >
                                        {network.name}
                                    </Link>
                                );
                            })
                        ) : (
                            <span className="movie-detail-more__muted">نامشخص</span>
                        )}
                    </MoreInfoRow>
                )}

                <MoreInfoRow label="رده سنی:">
                    {data.age_rating !== "not rated" ? (
                        <span className="movie-detail-more__pill">{data.age_rating}</span>
                    ) : (
                        <span className="movie-detail-more__muted">نامشخص</span>
                    )}
                </MoreInfoRow>

                <MovieAgents id={data.id} />
            </section>
        </>
    );
}

function MoreInfoRow(props: { label: string; children: React.ReactNode }) {
    return (
        <div className="movie-detail-more__row">
            <span className="movie-detail-more__label">{props.label}</span>
            <div className="movie-detail-more__line" />
            <div className="movie-detail-more__value">{props.children}</div>
        </div>
    );
}
